import { useState, useMemo, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BlogCard from "../components/BlogCard";
import BlogSidebar from "../components/BlogSidebar";
import SEOHead from "../components/SEOHead";
import { BLOG_POSTS } from "../data/blogPosts";
import { ChevronLeft, ChevronRight, Tag } from "lucide-react";
import SkeletonCard from "../components/SkeletonCard";

import { useTranslation } from 'react-i18next';
const POSTS_PER_PAGE = 6;

function slugify(str) {
  return str.toLowerCase().replace(/\s+/g, "-");
}

export default function BlogTag() {
  const { t } = useTranslation();
  const { tag: slug } = useParams();
  const tagSlug = decodeURIComponent(slug || "");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(timer);
  }, [tagSlug]);

  // Resolve slug to actual tag name from data
  const tagName = useMemo(() => {
    const allTags = new Set();
    BLOG_POSTS.forEach((p) => (p.tags || []).forEach((t) => allTags.add(t)));
    // Try direct match first (case-insensitive)
    for (const t of allTags) {
      if (t.toLowerCase() === tagSlug.toLowerCase()) return t;
    }
    // Try slugified match
    for (const t of allTags) {
      if (slugify(t) === tagSlug) return t;
    }
    // Fallback: deslugify
    return tagSlug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }, [tagSlug]);

  const tagPosts = useMemo(() => {
    return BLOG_POSTS.filter(
      (p) => (p.tags || []).includes(tagName)
    );
  }, [tagName]);

  const totalPages = Math.max(1, Math.ceil(tagPosts.length / POSTS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pagePosts = tagPosts.slice(
    (safePage - 1) * POSTS_PER_PAGE,
    safePage * POSTS_PER_PAGE
  );

  const description = `Browse all articles tagged with ${tagName} on the Max Employee blog. Insights, guides, and tutorials on AI digital workers.`;

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://maxemployee.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://maxemployee.com/blog" },
      { "@type": "ListItem", position: 3, name: `#${tagName}`, item: `https://maxemployee.com/blog/tag/${slugify(tagName)}` },
    ],
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <SEOHead
        title={`Articles tagged "${tagName}" | Max Employee Blog`}
        description={description}
        url={`/blog/tag/${slugify(tagName)}`}
        structuredData={breadcrumbStructuredData}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-zinc-900 bg-black">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#09090B]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-8 sm:pt-12 pb-8 sm:pb-12">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-amber-500 font-mono text-xs uppercase tracking-[0.2em] mb-6 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
          >
            <ChevronLeft className="w-4 h-4" aria-hidden="true" /> Back to Blog
          </Link>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
              <Tag className="w-5 h-5 text-amber-500" aria-hidden="true" />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">
              Tag
            </div>
          </div>

          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl uppercase leading-[0.9] text-white mb-4">
            #{tagName}
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 leading-relaxed max-w-2xl">
            {description}
          </p>
          <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            {tagPosts.length} article{tagPosts.length !== 1 ? "s" : ""}
          </div>
        </div>
      </section>

      <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Main content */}
          <div className="lg:col-span-8 order-2 lg:order-1">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" role="list" aria-label={t('blog_tag.loading_blog_posts')}>
                {Array.from({ length: POSTS_PER_PAGE }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            ) : pagePosts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" role="list" aria-label={t('blog_tag.blog_posts')}>
                {pagePosts.map((post) => (
                  <BlogCard key={post.slug} post={post} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 sm:py-20" role="status" aria-live="polite">
                <p className="font-display text-xl sm:text-2xl uppercase text-zinc-400 mb-4">{t('blog_tag.no_articles_with_this_tag')}</p>
                <p className="text-zinc-400 text-sm mb-6">{t('blog_tag.check_back_soon_or_explore_other_tags')}</p>
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-2 text-amber-500 hover:text-amber-400 font-mono text-xs uppercase tracking-[0.2em] transition-colors"
                >
                  View all articles
                </Link>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <nav className="flex items-center justify-center gap-2 sm:gap-4 mt-8 sm:mt-12" aria-label={t('blog_tag.pagination')}>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="flex items-center gap-1 px-3 sm:px-4 py-2 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed font-mono text-[10px] uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 min-h-[44px]"
                  aria-label={t('blog_tag.previous_page')}
                >
                  <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">{t('blog_tag.prev')}</span>
                </button>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                  Page {safePage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="flex items-center gap-1 px-3 sm:px-4 py-2 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 disabled:opacity-30 disabled:cursor-not-allowed font-mono text-[10px] uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 min-h-[44px]"
                  aria-label={t('blog_tag.next_page')}
                >
                  <span className="hidden sm:inline">{t('blog_tag.next')}</span> <ChevronRight className="w-4 h-4" />
                </button>
              </nav>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 order-1 lg:order-2" aria-label={t('blog_tag.blog_sidebar')}>
            <BlogSidebar />
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}
